/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import tw from 'twin.macro';
import { ReactComponent as ScanSearchSvg } from 'assets/Icons/scan-search.svg';
import { ReactComponent as ShareSvg } from 'assets/Icons/Share.svg';
import { RootState } from 'reducers';
import { SemanticSearchCounts } from './semanticSearchMapper';

export type SemanticSection = 'exact' | 'related';

export const SECTION_TITLE: Record<SemanticSection, string> = {
  exact: 'Exact matches',
  related: 'Related content',
};

// Keyed by transcript_search's highlight.kind (highlight.py HighlightDraft).
// Leading means no close match, shown with related hits.
const KIND_SECTION: Record<string, SemanticSection> = {
  lexical: 'exact',
  semantic: 'related',
  leading: 'related',
};

/** Section a result's highlight kind belongs to; null for unknown kinds. */
export const semanticKindSection = (kind?: string): SemanticSection | null =>
  (kind && KIND_SECTION[kind.toLowerCase()]) || null;

const BADGE_LABEL: Record<SemanticSection, string> = {
  exact: 'Exact match',
  related: 'Related',
};

export const mentionCountLabel = (count: number): string =>
  `${count.toLocaleString()} ${count === 1 ? 'mention' : 'mentions'}`;

type SemanticKindBadgeProps = {
  kind?: string;
  mentionCount?: number | null;
};

/** Card badge for a result's highlight kind; nothing for unknown kinds. */
export const SemanticKindBadge = ({
  kind,
  mentionCount,
}: SemanticKindBadgeProps) => {
  const section = semanticKindSection(kind);
  if (!section) return null;

  const isExact = section === 'exact';
  return (
    <span tw="inline-flex items-center gap-4">
      <span
        tw="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-12 font-medium"
        css={isExact ? exactIconCss : relatedIconCss}
      >
        {isExact ? <ScanSearchSvg tw="w-5 h-5" /> : <ShareSvg tw="w-4 h-4" />}
        {BADGE_LABEL[section]}
      </span>
      {isExact && mentionCount != null && (
        <span tw="text-12 font-medium text-sonnant-grey-6">
          {mentionCountLabel(mentionCount)}
        </span>
      )}
    </span>
  );
};

/** Section to open before the card at `index`, or null. `exactMatchOnPage`
 * is null outside semantic search, where results are not split. */
export const semanticSectionBefore = (
  index: number,
  exactMatchOnPage: number | null | undefined,
): SemanticSection | null => {
  if (exactMatchOnPage == null) return null;
  if (index === 0 && exactMatchOnPage > 0) return 'exact';
  if (index === exactMatchOnPage) return 'related';
  return null;
};

const SECTION_COUNT_KEY: Record<SemanticSection, keyof SemanticSearchCounts> = {
  exact: 'lexical',
  related: 'semantic',
};

type Props = {
  section: SemanticSection;
};

export const SemanticSectionHeading = ({ section }: Props) => {
  const searchTerm = useSelector(
    (state: RootState) => state.library.searchTerm as string,
  );
  const counts = useSelector(
    (state: RootState) =>
      state.library.data?.semanticCounts as SemanticSearchCounts | undefined,
  );
  const isExact = section === 'exact';

  return (
    <div tw="flex items-center gap-4 py-3 pl-4">
      <div
        tw="flex items-center justify-center flex-shrink-0 w-14 h-14 rounded-lg"
        css={isExact ? exactIconCss : relatedIconCss}
      >
        {isExact ? <ScanSearchSvg tw="w-8 h-8" /> : <ShareSvg tw="w-7 h-7" />}
      </div>
      <div>
        <div tw="flex items-center gap-2">
          <span tw="text-18 font-medium text-sonnant-dark">
            {SECTION_TITLE[section]}
          </span>
          {counts && (
            <span css={countBadgeCss}>
              {counts[SECTION_COUNT_KEY[section]].toLocaleString()}
            </span>
          )}
        </div>
        <div tw="text-14 text-sonnant-grey-5">
          {isExact
            ? `Items that contain “${searchTerm}”. Every match is here.`
            : 'About the same topic, without your exact words.'}
        </div>
      </div>
    </div>
  );
};

export const countBadgeCss = tw`inline-flex items-center px-2 rounded-full text-12 font-medium bg-sonnant-grey-3 text-sonnant-grey-6`;

const exactIconCss = css`
  ${tw`text-sonnant-purple-2`}
  background-color: rgba(85, 81, 255, 0.12);
`;

const relatedIconCss = css`
  ${tw`text-sonnant-green`}
  background-color: rgba(25, 175, 167, 0.12);
  path {
    fill: #19afa7;
  }
`;
